import html2pdf from 'html2pdf.js';
import pptxgen from 'pptxgenjs';
import * as XLSX from 'xlsx';

/* html-docx-js uses `with` statements which esbuild refuses to transform
 * for ES modules (strict mode). Loading it as a plain <script> drops it
 * into sloppy mode so the `with` works.
 *
 * Self-host the file under /lib/html-docx.js (copied from
 * node_modules/html-docx-js/dist/ at build time — see public/lib/) so we
 * don't depend on an external CDN. The original CDN path (unpkg.com)
 * was BLOCKED by the prod CSP (script-src 'self' + js.stripe.com only),
 * so DOCX exports silently hung forever in production — caught in the
 * synth-export audit. Self-hosting fixes that without weakening CSP. */
let _htmlDocxPromise: Promise<any> | null = null;
function loadHtmlDocx(): Promise<any> {
  if (_htmlDocxPromise) return _htmlDocxPromise;
  _htmlDocxPromise = new Promise((resolve, reject) => {
    if ((window as any).htmlDocx) return resolve((window as any).htmlDocx);
    const script = document.createElement('script');
    script.src = '/lib/html-docx.js';
    script.async = true;
    script.onload = () => {
      const lib = (window as any).htmlDocx;
      if (lib) resolve(lib);
      else { _htmlDocxPromise = null; reject(new Error('html-docx-js loaded but global missing')); }
    };
    script.onerror = () => { _htmlDocxPromise = null; reject(new Error('Failed to load html-docx-js from /lib/')); };
    document.head.appendChild(script);
  });
  return _htmlDocxPromise;
}
import { MODELS } from '../data/models';
import { PROVIDERS } from '../data/providers';
import { SYNTH_MODES } from '../data/synth-modes';
import { state, savedState } from './state';
import { t, getCurrentLang } from './i18n';
import { escapeHtml, nowReadable, nowStamp, slugify, mdToHtml, stripMarkdown, truncate, downloadText, saveBlobAs } from './ui-helpers';
import { appendDeliverable } from './history-client';
import { triggerAutosave } from './autosave';
import { toast } from './toast';

/* =================================================================
   buildResponseMarkdown / buildAllResponsesMarkdown / buildSynthMarkdown / buildOptimalPromptMarkdown
   ================================================================= */
export function buildResponseMarkdown(out) {
  const m = MODELS.find(x => x.id === out.modelId);
  const provider = m ? PROVIDERS[m.provider].label : "Unknown";
  const label = m ? m.label : out.modelId;
  return `# Lorenza · ${label}

**Date:** ${nowReadable()}
**Provider:** ${provider}
**Model:** \`${out.modelId}\`
**Latency:** ${out.ms}ms

## Prompt
${state.lastPrompt}

## Response
${out.text || "(no content)"}
`;
}

export function buildAllResponsesMarkdown() {
  const ok = state.lastResponses.filter(r => r.ok);
  if (ok.length === 0) return "";
  const lines = [];
  lines.push("# Lorenza · Multi-Model Comparison");
  lines.push("");
  lines.push(`**Date:** ${nowReadable()}`);
  lines.push(`**Models:** ${ok.map(r => {
    const m = MODELS.find(x => x.id === r.modelId);
    return m ? m.label : r.modelId;
  }).join(", ")}`);
  lines.push("");
  lines.push("## Original Prompt");
  lines.push(state.lastPrompt);
  lines.push("");
  lines.push("## Responses");
  lines.push("");
  ok.forEach(r => {
    const m = MODELS.find(x => x.id === r.modelId);
    const label = m ? m.label : r.modelId;
    const provider = m ? PROVIDERS[m.provider].label : "";
    lines.push(`### ${label} (${provider} · ${r.ms}ms)`);
    lines.push("");
    lines.push(r.text || "(no content)");
    lines.push("");
  });
  // Include errors as appendix if any
  const errs = state.lastResponses.filter(r => !r.ok);
  if (errs.length > 0) {
    lines.push("## Errors");
    lines.push("");
    errs.forEach(r => {
      const m = MODELS.find(x => x.id === r.modelId);
      lines.push(`- **${m ? m.label : r.modelId}**: ${r.err}`);
    });
  }
  return lines.join("\n");
}

export function buildSynthMarkdown(synthText, modeKey, synthModelId) {
  const synthModel = MODELS.find(m => m.id === synthModelId);
  const sources = state.lastResponses.filter(r => r.ok).map(r => {
    const m = MODELS.find(x => x.id === r.modelId);
    return m ? m.label : r.modelId;
  });
  const modeLabel = SYNTH_MODES[modeKey]?.labels[getCurrentLang()] || modeKey;
  return `# Lorenza · Synthesis

**Date:** ${nowReadable()}
**Mode:** ${modeLabel}
**Synthesizer:** ${synthModel ? synthModel.label : synthModelId}
**Source Models:** ${sources.join(", ")}

## Original Prompt
${state.lastPrompt}

## Synthesis
${synthText}
`;
}

export function buildOptimalPromptMarkdown(promptText, modeKey) {
  const modeLabel = SYNTH_MODES[modeKey]?.labels[getCurrentLang()] || modeKey;
  return `# Lorenza · Optimal Prompt

**Date:** ${nowReadable()}
**Reverse-engineered from synthesis mode:** ${modeLabel}

## Original Prompt
${state.lastPrompt}

## Optimal Prompt (recommended for future single-shot use)
${promptText}
`;
}

/* =========================================================
   Multi-format export helpers
   ========================================================= */

/* Build a clean, print-ready HTML doc with Lorenza branding */
export function buildPrintHtml(title, sections) {
  const body = sections.map(s => {
    let h = "";
    if (s.heading) h += `<h2>${escapeHtml(s.heading)}</h2>`;
    if (s.subhead) h += `<div class="subhead">${escapeHtml(s.subhead)}</div>`;
    if (s.markdown) h += mdToHtml(s.markdown);
    else if (s.html) h += s.html;
    else if (s.text) h += `<pre>${escapeHtml(s.text)}</pre>`;
    return `<section>${h}</section>`;
  }).join("\n");

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  /* Selectors duplicated as 'body, .print-doc' so the same template renders correctly
     for html-docx-js (which sees a full HTML document and applies 'body' rules) AND
     for html2pdf, where the markup is injected as innerHTML into a wrapper div and
     bare 'body' selectors would either apply to the live page body (wrong colors) or
     not apply at all. The '.print-doc' mirror guarantees scoped, self-contained styling
     during PDF capture. */
  body, .print-doc { font-family: 'Calibri', 'Segoe UI', Arial, sans-serif; color: #1F2937; background: #ffffff; max-width: 720px; margin: 0 auto; padding: 32px; line-height: 1.55; }
  body h1, .print-doc h1 { font-size: 24px; color: #0B1120; border-bottom: 2px solid #00B873; padding-bottom: 10px; margin-bottom: 6px; }
  body h2, .print-doc h2 { font-size: 17px; color: #0B1120; margin-top: 22px; margin-bottom: 8px; }
  body h3, .print-doc h3 { font-size: 14px; color: #0B1120; margin-top: 14px; margin-bottom: 6px; }
  body .meta, .print-doc .meta { color: #6B7280; font-size: 11px; margin-bottom: 18px; }
  body .subhead, .print-doc .subhead { color: #6B7280; font-size: 12px; margin: 2px 0 10px; font-style: italic; }
  body p, .print-doc p { margin: 0 0 10px; color: #1F2937; }
  body ul, body ol, .print-doc ul, .print-doc ol { margin: 0 0 10px 22px; color: #1F2937; }
  body li, .print-doc li { margin-bottom: 3px; color: #1F2937; }
  body code, .print-doc code { font-family: 'Consolas', monospace; font-size: 12px; background: #F3F4F6; color: #1F2937; padding: 1px 5px; border-radius: 3px; }
  body pre, .print-doc pre { font-family: 'Consolas', monospace; font-size: 11px; background: #F3F4F6; color: #1F2937; padding: 10px; border-radius: 4px; overflow-x: auto; white-space: pre-wrap; }
  body blockquote, .print-doc blockquote { border-left: 3px solid #D1D5DB; padding-left: 10px; color: #4B5563; margin: 0 0 10px; }
  body table, .print-doc table { border-collapse: collapse; width: 100%; margin: 8px 0 12px; font-size: 12px; color: #1F2937; }
  body th, body td, .print-doc th, .print-doc td { border: 1px solid #D1D5DB; padding: 6px 9px; text-align: left; }
  body th, .print-doc th { background: #F3F4F6; }
  body .footer, .print-doc .footer { margin-top: 32px; padding-top: 14px; border-top: 1px solid #D1D5DB; font-size: 10px; color: #6B7280; text-align: center; }
</style></head><body>
<div class="print-doc">
<h1>${escapeHtml(title)}</h1>
<div class="meta">Lorenza · Compare · ${nowReadable()}</div>
${body}
<div class="footer">Generated by Lorenza · Lopettia.ai · Compare Surface</div>
</div>
</body></html>`;
}

/* ---- Word ---- */
export async function exportDocx(filename, title, sections) {
  let htmlDocx;
  try {
    htmlDocx = await loadHtmlDocx();
  } catch (e) {
    toast(t("export.libmissing", { fmt: "Word" }));
    return { ok: false, error: e };
  }
  const html = buildPrintHtml(title, sections);
  const blob = htmlDocx.asBlob(html);
  return saveBlobAs(blob, filename);
}

/* ---- PDF ---- */
export async function exportPdf(filename, title, sections) {
  if (typeof html2pdf === "undefined") {
    toast(t("export.libmissing", { fmt: "PDF" }));
    return { ok: false, error: new Error("html2pdf undefined") };
  }
  const html = buildPrintHtml(title, sections);
  const wrap = document.createElement("div");
  /* Off-screen via absolute (not fixed) positioning so the element is in normal
     document flow and html2canvas measures its full intrinsic height correctly.
     Explicit white background prevents the live page's dark theme from bleeding
     through where the wrap content has transparent backgrounds. */
  wrap.style.cssText = "position: absolute; top: -10000px; left: 0; width: 800px; background: #ffffff; color: #1F2937; z-index: -1;";
  wrap.innerHTML = html;
  document.body.appendChild(wrap);
  try {
    /* Give the browser one frame to lay out the wrap content and any embedded
       <style>; without this, html2canvas occasionally captures a 0-height clone. */
    await new Promise(r => requestAnimationFrame(() => setTimeout(r, 80)));
    /* Get a Blob from html2pdf instead of letting it call its internal .save(),
       so we can route the file through saveBlobAs and get a true Save-As dialog. */
    const blob = await html2pdf().set({
      margin: [12, 12, 12, 12],
      filename,
      image: { type: "jpeg", quality: 0.95 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    }).from(wrap).output("blob");
    return await saveBlobAs(blob, filename);
  } catch (e) {
    console.error("[exportPdf]", e);
    toast(t("export.failed", { fmt: "PDF" }));
    return { ok: false, error: e };
  } finally {
    document.body.removeChild(wrap);
  }
}

/* ---- Excel ---- */
export async function exportXlsxAll(filename) {
  if (typeof XLSX === "undefined") {
    toast(t("export.libmissing", { fmt: "Excel" }));
    return { ok: false, error: new Error("XLSX undefined") };
  }
  const ok = state.lastResponses.filter(r => r.ok);
  if (ok.length === 0) { toast(t("save.nothing")); return { ok: false, error: new Error("nothing to save") }; }
  const wb = XLSX.utils.book_new();

  // Sheet 1: Side-by-side comparison
  const headers = ["Field"].concat(ok.map(r => {
    const m = MODELS.find(x => x.id === r.modelId);
    return m ? m.label : r.modelId;
  }));
  const rows = [headers];
  rows.push(["Provider"].concat(ok.map(r => {
    const m = MODELS.find(x => x.id === r.modelId);
    return m ? PROVIDERS[m.provider].label : "";
  })));
  rows.push(["Model ID"].concat(ok.map(r => r.modelId)));
  rows.push(["Latency (ms)"].concat(ok.map(r => r.ms)));
  rows.push(["Response chars"].concat(ok.map(r => (r.text || "").length)));
  rows.push(["Response"].concat(ok.map(r => r.text || "")));
  const ws1 = XLSX.utils.aoa_to_sheet(rows);
  ws1["!cols"] = [{ wch: 18 }].concat(ok.map(() => ({ wch: 60 })));
  XLSX.utils.book_append_sheet(wb, ws1, "Comparison");

  // Sheet 2: Metadata + prompt
  const metaRows = [
    ["Lorenza · Compare — Multi-Model Run"],
    [""],
    ["Date", nowReadable()],
    ["Models", ok.length],
    ["Successful", ok.length],
    ["Failed", state.lastResponses.length - ok.length],
    [""],
    ["Original Prompt"],
    [state.lastPrompt],
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(metaRows);
  ws2["!cols"] = [{ wch: 18 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(wb, ws2, "Metadata");

  // Sheet 3: Errors (if any)
  const errs = state.lastResponses.filter(r => !r.ok);
  if (errs.length > 0) {
    const errRows = [["Model", "Provider", "Status", "Error"]];
    errs.forEach(e => {
      const m = MODELS.find(x => x.id === e.modelId);
      errRows.push([m ? m.label : e.modelId, e.provider || "", e.status || "", e.err || ""]);
    });
    const ws3 = XLSX.utils.aoa_to_sheet(errRows);
    ws3["!cols"] = [{ wch: 22 }, { wch: 14 }, { wch: 10 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, ws3, "Errors");
  }

  /* Generate the workbook as an array buffer and route through saveBlobAs
     instead of XLSX.writeFile, which calls .save() internally with no
     filename picker. */
  const arrayBuffer = XLSX.write(wb, { type: "array", bookType: "xlsx" });
  const blob = new Blob([arrayBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  return saveBlobAs(blob, filename);
}

/* ---- PowerPoint ----
   level: "detailed" | "summary" | "executive"
   - detailed: title + prompt + 1 slide per response + synth slide
   - summary: title + prompt + 1 condensed slide per response (truncated) + synth
   - executive: title + key conclusion + 1 highlights slide + recommendation
*/
export async function exportPptx(filename, level) {
  const PptxGenJS = pptxgen;
  if (typeof PptxGenJS === "undefined") {
    toast(t("export.libmissing", { fmt: "PowerPoint" }));
    return { ok: false, error: new Error("pptxgenjs undefined") };
  }
  const ok = state.lastResponses.filter(r => r.ok);
  if (ok.length === 0 && !savedState.lastSynthText) {
    toast(t("save.nothing"));
    return { ok: false, error: new Error("nothing to save") };
  }
  const pptx = new PptxGenJS();
  pptx.author = "Lorenza · Lopettia.ai";
  pptx.company = "Lopettia, LLC";
  pptx.title = "Lorenza · Compare";
  pptx.layout = "LAYOUT_WIDE";

  // Lorenza brand colors
  const C_INK = "0B1120";
  const C_MATRIX = "00FF9C";
  const C_CORAL = "F38281";
  const C_GOLD = "FCE28A";
  const C_TEXT = "F2F0EB";
  const C_MUTED = "9CA3AF";

  // ---------- Master / cover ----------
  const titleSlide = pptx.addSlide();
  titleSlide.background = { color: C_INK };
  titleSlide.addText("LORENZA", {
    x: 0.5, y: 1.6, w: 12, h: 1.0,
    fontSize: 56, fontFace: "Calibri", bold: true,
    color: C_TEXT, align: "left", charSpacing: 8,
  });
  titleSlide.addText("Compare · Synthesize · Decide.", {
    x: 0.5, y: 2.5, w: 12, h: 0.6,
    fontSize: 22, fontFace: "Calibri", italic: true,
    color: C_MATRIX, align: "left",
  });
  titleSlide.addText(`Multi-Model AI Comparison — ${nowReadable()}`, {
    x: 0.5, y: 3.3, w: 12, h: 0.4,
    fontSize: 14, fontFace: "Calibri", color: C_MUTED, align: "left",
  });
  titleSlide.addText(level.toUpperCase() + " REPORT", {
    x: 0.5, y: 6.4, w: 12, h: 0.4,
    fontSize: 11, fontFace: "Consolas", color: C_GOLD, align: "left", charSpacing: 4,
  });
  titleSlide.addText("Lopettia.ai · San Juan, Puerto Rico", {
    x: 0.5, y: 6.9, w: 12, h: 0.3,
    fontSize: 10, fontFace: "Calibri", color: C_MUTED, align: "left",
  });

  // ---------- Prompt slide (in detailed + summary) ----------
  if (level !== "executive") {
    const ps = pptx.addSlide();
    ps.background = { color: C_INK };
    ps.addText("Prompt", {
      x: 0.5, y: 0.3, w: 12, h: 0.5,
      fontSize: 24, fontFace: "Calibri", bold: true, color: C_MATRIX,
    });
    ps.addText(state.lastPrompt || "(no prompt)", {
      x: 0.5, y: 1.0, w: 12, h: 5.8,
      fontSize: 16, fontFace: "Calibri", color: C_TEXT,
      valign: "top", isTextBox: true,
    });
    ps.addText("Models: " + ok.map(r => {
      const m = MODELS.find(x => x.id === r.modelId);
      return m ? m.label : r.modelId;
    }).join(" · "), {
      x: 0.5, y: 6.9, w: 12, h: 0.4,
      fontSize: 11, fontFace: "Consolas", color: C_GOLD,
    });
  }

  // ---------- Per-model response slides ----------
  if (level === "detailed") {
    ok.forEach(r => {
      const m = MODELS.find(x => x.id === r.modelId);
      const label = m ? m.label : r.modelId;
      const provider = m ? PROVIDERS[m.provider].label : "";
      const slide = pptx.addSlide();
      slide.background = { color: C_INK };
      slide.addText(label, {
        x: 0.5, y: 0.3, w: 9, h: 0.55,
        fontSize: 24, fontFace: "Calibri", bold: true, color: C_TEXT,
      });
      slide.addText(`${provider} · ${r.ms}ms`, {
        x: 0.5, y: 0.85, w: 9, h: 0.35,
        fontSize: 11, fontFace: "Consolas", color: C_MATRIX,
      });
      slide.addText(stripMarkdown(r.text), {
        x: 0.5, y: 1.4, w: 12, h: 5.5,
        fontSize: 12, fontFace: "Calibri", color: C_TEXT, valign: "top",
        isTextBox: true,
      });
    });
  } else if (level === "summary") {
    // 2 cards per slide, condensed
    for (let i = 0; i < ok.length; i += 2) {
      const slide = pptx.addSlide();
      slide.background = { color: C_INK };
      slide.addText("Responses", {
        x: 0.5, y: 0.3, w: 12, h: 0.5,
        fontSize: 22, fontFace: "Calibri", bold: true, color: C_MATRIX,
      });
      [0, 1].forEach(j => {
        const r = ok[i + j];
        if (!r) return;
        const m = MODELS.find(x => x.id === r.modelId);
        const label = m ? m.label : r.modelId;
        const x = j === 0 ? 0.5 : 6.7;
        slide.addText(label, {
          x, y: 1.0, w: 5.8, h: 0.4,
          fontSize: 16, fontFace: "Calibri", bold: true, color: C_TEXT,
        });
        slide.addText(truncate(stripMarkdown(r.text), 800), {
          x, y: 1.5, w: 5.8, h: 5.4,
          fontSize: 10, fontFace: "Calibri", color: C_TEXT, valign: "top",
          isTextBox: true,
        });
      });
    }
  }
  // Executive: skip per-model slides

  // ---------- Synthesis slide ----------
  if (savedState.lastSynthText) {
    const synth = pptx.addSlide();
    synth.background = { color: C_INK };
    const synthTitle = level === "executive"
      ? "Executive Conclusion"
      : "Synthesis";
    synth.addText(synthTitle, {
      x: 0.5, y: 0.3, w: 12, h: 0.55,
      fontSize: 26, fontFace: "Calibri", bold: true, color: C_CORAL,
    });
    const modeLabel = SYNTH_MODES[savedState.lastSynthMode]?.labels[getCurrentLang()] || savedState.lastSynthMode;
    synth.addText(`Mode: ${modeLabel}`, {
      x: 0.5, y: 0.9, w: 12, h: 0.35,
      fontSize: 11, fontFace: "Consolas", color: C_GOLD,
    });
    const body = level === "executive"
      ? truncate(stripMarkdown(savedState.lastSynthText), 1400)
      : stripMarkdown(savedState.lastSynthText);
    synth.addText(body, {
      x: 0.5, y: 1.4, w: 12, h: 5.5,
      fontSize: level === "executive" ? 16 : 13, fontFace: "Calibri",
      color: C_TEXT, valign: "top", isTextBox: true,
    });
  }

  // ---------- Closing ----------
  const close = pptx.addSlide();
  close.background = { color: C_INK };
  close.addText("Lorenza", {
    x: 0.5, y: 3.0, w: 12, h: 0.8,
    fontSize: 44, fontFace: "Calibri", bold: true, color: C_TEXT, align: "center",
    charSpacing: 6,
  });
  close.addText("AI in action. Now.", {
    x: 0.5, y: 4.0, w: 12, h: 0.5,
    fontSize: 18, fontFace: "Calibri", italic: true, color: C_MATRIX, align: "center",
  });
  close.addText("lopettia.ai", {
    x: 0.5, y: 6.8, w: 12, h: 0.3,
    fontSize: 11, fontFace: "Consolas", color: C_GOLD, align: "center",
  });

  /* pptxgenjs supports `.write()` which returns a Promise<Blob | base64>.
     Request blob output and route through saveBlobAs for a true Save-As dialog. */
  const blob = await pptx.write({ outputType: "blob" });
  return saveBlobAs(blob, filename);
}

/* ============================================================
   Export menu factory — attaches a dropdown to a button
   `formats` selects which options to show
   `getContext()` returns the data to export at click time
   ============================================================ */
export function mountExportMenu(buttonEl, formats, getContext, baseName) {
  // Mark the button as an export-menu anchor (used by the global outside-click handler)
  buttonEl.dataset.exportAnchor = "true";

  // Remove any prior menu attached to this exact button
  if (buttonEl._exportMenu) {
    buttonEl._exportMenu.remove();
    buttonEl._exportMenu = null;
  }

  // Attach the menu to <body> directly so it escapes any backdrop-filter / transform
  // / will-change containing block created by an ancestor panel.
  const menu = document.createElement("div");
  menu.className = "export-menu";
  const items = [];

  if (formats.includes("md"))
    items.push(`<button data-fmt="md"><span class="fmt-icon fmt-md">MD</span> ${t("export.md")}<span class="fmt-sub">.md</span></button>`);
  if (formats.includes("txt"))
    items.push(`<button data-fmt="txt"><span class="fmt-icon fmt-md">TXT</span> ${t("export.txt")}<span class="fmt-sub">.txt</span></button>`);
  if (formats.includes("docx"))
    items.push(`<button data-fmt="docx"><span class="fmt-icon fmt-docx">W</span> ${t("export.docx")}<span class="fmt-sub">.docx</span></button>`);
  if (formats.includes("pdf"))
    items.push(`<button data-fmt="pdf"><span class="fmt-icon fmt-pdf">PDF</span> ${t("export.pdf")}<span class="fmt-sub">.pdf</span></button>`);
  if (formats.includes("xlsx"))
    items.push(`<button data-fmt="xlsx"><span class="fmt-icon fmt-xlsx">X</span> ${t("export.xlsx")}<span class="fmt-sub">.xlsx</span></button>`);
  if (formats.includes("pptx-detailed") || formats.includes("pptx-summary") || formats.includes("pptx-executive")) {
    items.push(`<hr><div class="menu-section">${t("export.pptx.section")}</div>`);
    if (formats.includes("pptx-detailed"))
      items.push(`<button data-fmt="pptx-detailed"><span class="fmt-icon fmt-pptx">P</span> ${t("export.pptx.detailed")}<span class="fmt-sub">.pptx</span></button>`);
    if (formats.includes("pptx-summary"))
      items.push(`<button data-fmt="pptx-summary"><span class="fmt-icon fmt-pptx">P</span> ${t("export.pptx.summary")}<span class="fmt-sub">.pptx</span></button>`);
    if (formats.includes("pptx-executive"))
      items.push(`<button data-fmt="pptx-executive"><span class="fmt-icon fmt-pptx">P</span> ${t("export.pptx.executive")}<span class="fmt-sub">.pptx</span></button>`);
  }
  menu.innerHTML = items.join("");
  document.body.appendChild(menu);
  buttonEl._exportMenu = menu;

  function positionMenu() {
    const rect = buttonEl.getBoundingClientRect();
    // Reset any previous positioning so we can measure
    menu.style.top = "0px";
    menu.style.left = "0px";
    menu.style.right = "auto";
    menu.style.bottom = "auto";
    menu.style.visibility = "hidden";
    menu.classList.add("open");
    const menuRect = menu.getBoundingClientRect();
    menu.classList.remove("open");
    menu.style.visibility = "";

    const margin = 6;
    // Right-align to the button's right edge by default
    let left = rect.right - menuRect.width;
    // Clamp horizontally inside viewport
    if (left < 8) left = 8;
    if (left + menuRect.width > window.innerWidth - 8) {
      left = window.innerWidth - menuRect.width - 8;
    }
    // Open downward by default; flip up if it would overflow
    let top = rect.bottom + margin;
    if (top + menuRect.height > window.innerHeight - 8) {
      top = rect.top - menuRect.height - margin;
    }
    if (top < 8) top = 8;
    menu.style.left = left + "px";
    menu.style.top = top + "px";
  }

  buttonEl.addEventListener("click", e => {
    e.stopPropagation();
    document.querySelectorAll(".export-menu.open").forEach(el => {
      if (el !== menu) el.classList.remove("open");
    });
    if (menu.classList.contains("open")) {
      menu.classList.remove("open");
    } else {
      positionMenu();
      menu.classList.add("open");
    }
  });

  // Reposition (don't close) on scroll/resize so the menu follows its anchor.
  // Only close if the anchor scrolled completely out of view.
  const onViewportChange = () => {
    if (!menu.classList.contains("open")) return;
    const rect = buttonEl.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      menu.classList.remove("open");
    } else {
      positionMenu();
    }
  };
  window.addEventListener("scroll", onViewportChange, { passive: true });
  window.addEventListener("resize", onViewportChange);

  menu.addEventListener("click", async e => {
    const btn = e.target.closest("button[data-fmt]");
    if (!btn) return;
    const fmt = btn.dataset.fmt;
    menu.classList.remove("open");
    const ctx = getContext();
    if (!ctx) { toast(t("save.nothing")); return; }
    const stamp = nowStamp();
    const fname = `${baseName}-${stamp}`;
    try {
      let result;
      // Compute the actual saved filename once so the deliverable record
      // matches what really hit disk (PPTX adds a -<level> infix).
      let savedFilename = '';
      if (fmt === "md") {
        savedFilename = `${fname}.md`;
        result = await downloadText(savedFilename, ctx.markdown);
      } else if (fmt === "txt") {
        savedFilename = `${fname}.txt`;
        result = await downloadText(savedFilename, ctx.plainText || ctx.markdown, "text/plain;charset=utf-8");
      } else if (fmt === "docx") {
        savedFilename = `${fname}.docx`;
        result = await exportDocx(savedFilename, ctx.title, ctx.sections);
      } else if (fmt === "pdf") {
        savedFilename = `${fname}.pdf`;
        result = await exportPdf(savedFilename, ctx.title, ctx.sections);
      } else if (fmt === "xlsx") {
        savedFilename = `${fname}.xlsx`;
        result = await exportXlsxAll(savedFilename);
      } else if (fmt.startsWith("pptx-")) {
        const level = fmt.split("-")[1];
        savedFilename = `${fname}-${level}.pptx`;
        result = await exportPptx(savedFilename, level);
      }
      /* User cancelled the Save-As picker → no toast (silence is correct) */
      if (result && result.cancelled) return;
      /* Internal error already toasted by the exporter → don't double-toast.
         If `result` is undefined (a defensive return slipped through), treat
         it as failure so we don't record a phantom deliverable. */
      if (!result || result.ok === false) return;
      toast(t("save.done", { name: ctx.title }));

      /* Persist the deliverable in the user's history (best-effort).
         Metadata only — the actual file bytes already left the page via
         the user's Save-As picker. */
      const fileExt = fmt.startsWith('pptx-') ? 'pptx' : fmt;
      const iid = (state as any).lastInteractionId;
      if (iid) {
        appendDeliverable(iid, {
          kind: fileExt,
          name: savedFilename,
          metadata: { title: ctx.title, fmt },
        }).catch(() => { /* already logged */ });
      }
      // Mirror to per-session deliverables list so saved snapshots replay
      // a complete picture of what the user produced.
      (state as any).deliverables.push({
        kind: fileExt,
        name: savedFilename,
        title: ctx.title,
        created_at: new Date().toISOString(),
      });
      triggerAutosave();
    } catch (err) {
      toast(t("export.failed", { fmt }) + ": " + (err.message || err));
    }
  });
}

export function attachExportsGlobalHandlers() {
  // Close any open export menu on outside click (anchor button or menu itself excluded)
  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-export-anchor]")) return;
    if (e.target.closest(".export-menu")) return;
    document.querySelectorAll(".export-menu.open").forEach(el => el.classList.remove("open"));
  });

  // Escape key closes any open menu
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      document.querySelectorAll(".export-menu.open").forEach(el => el.classList.remove("open"));
    }
  });
}

/* attachExports wires up the "save all responses" dropdown that runner.ts
   triggers. It's idempotent — safe to call once at boot. The "save synth"
   and "save optimal" menus are mounted by their own modules. */
export function attachExports() {
  attachExportsGlobalHandlers();

  // Save all responses — multi-format dropdown
  mountExportMenu(
    document.getElementById("save-all-btn"),
    ["md", "docx", "pdf", "xlsx", "pptx-detailed", "pptx-summary", "pptx-executive"],
    () => {
      const ok = state.lastResponses.filter(r => r.ok);
      if (ok.length === 0) return null;
      const md = buildAllResponsesMarkdown();
      const sections = [
        { heading: t("export.section.prompt"), markdown: state.lastPrompt },
        { heading: t("export.section.responses") },
      ];
      ok.forEach(r => {
        const m = MODELS.find(x => x.id === r.modelId);
        const label = m ? m.label : r.modelId;
        const provider = m ? PROVIDERS[m.provider].label : "";
        sections.push({
          heading: label,
          subhead: `${provider} · ${r.ms}ms`,
          markdown: r.text,
        });
      });
      return { title: "Lorenza · Multi-Model Comparison", markdown: md, sections };
    },
    "lorenza-all-responses"
  );
}
